import { getPosts } from '@/lib/content';
import PageMeta from '@/shared/components/PageMeta';
import AcademicLayout from '@/shared/components/AcademicLayout';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { formatDate, getText } from '@/shared/utils/i18n';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' }
  }
};

export default function PostsPage() {
  const { language, t } = useLanguage();
  const posts = useMemo(() => getPosts(language), [language]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    posts.forEach(post => post.meta.category && set.add(post.meta.category));
    return Array.from(set);
  }, [posts]);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // 分类文案随语言变化（'Research' vs '研究'），切换语言时重置筛选
  useEffect(() => {
    setActiveCategory(null);
  }, [language]);

  const filteredPosts = activeCategory
    ? posts.filter(post => post.meta.category === activeCategory)
    : posts;

  return (
    <AcademicLayout>
      <PageMeta title="Writing" description={getText('postsSubtitle', 'en')} path="/posts" />
      <motion.div className="space-y-12" variants={containerVariants} initial="hidden" animate="visible">
        <motion.section variants={itemVariants}>
          <h1 className="text-2xl font-light text-foreground mb-3">{t('postsTitle')}</h1>
          <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-2xl">
            {t('postsSubtitle')}
          </p>
        </motion.section>

        {categories.length > 1 && (
          <motion.section variants={itemVariants} className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                activeCategory === null
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border/50 hover:border-border'
              }`}
            >
              {t('allCategories')}
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                  activeCategory === category
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border/50 hover:border-border'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.section>
        )}

        <motion.section variants={itemVariants} className="space-y-6">
          {filteredPosts.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('noPosts')}</p>
          )}
          {filteredPosts.map(post => (
            <motion.article
              key={post.slug}
              className="group p-4 -mx-4 rounded-lg hover:bg-muted/20 transition-all duration-300"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Link to={`/posts/${post.slug}`} className="block space-y-2">
                <h2 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                  {post.meta.title}
                </h2>
                <div className="flex items-center gap-3 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(post.meta.date, language)}
                  </span>
                  {post.meta.category && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {post.meta.category}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground/80 leading-relaxed">{post.meta.summary}</p>
                <span className="inline-flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('readMore')}
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </motion.article>
          ))}
        </motion.section>
      </motion.div>
    </AcademicLayout>
  );
}
